/**
 * Page « Assistance » du dashboard client (/dashboard/assistance).
 *
 * Formulaire de contact (sujet + message) + coordonnées BCL. L'envoi est
 * pour l'instant un mailto vers le support, pas d'endpoint dédié côté API.
 */
import { Mail, Phone } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';

const SUPPORT_EMAIL = 'support@bcl-telecom.fr';
const SUPPORT_PHONE = '+33 (0)4 22 13 25 00';

export function DashboardAssistancePage() {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmedSubject = subject.trim();
    const trimmedMessage = message.trim();
    if (!trimmedSubject || !trimmedMessage) {
      toast.error('Sujet et message obligatoires');
      return;
    }
    const href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(trimmedSubject)}&body=${encodeURIComponent(trimmedMessage)}`;
    window.location.assign(href);
    toast.success('Ouverture de votre client mail…');
    setSubject('');
    setMessage('');
  }

  return (
    <DashboardLayout title="Assistance">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Nous écrire</CardTitle>
            <CardDescription>
              Décrivez votre demande, notre équipe vous répond sous 48 h.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-1.5">
                <Label htmlFor="assistance-subject">Sujet</Label>
                <Input
                  id="assistance-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Question sur mon dossier BCL-26-…"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="assistance-message">Message</Label>
                <Textarea
                  id="assistance-message"
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Bonjour, je souhaite des précisions sur…"
                />
              </div>
              <Button type="submit">Envoyer</Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Coordonnées BCL</CardTitle>
            <CardDescription>
              Préférez un échange direct ? Contactez-nous.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-bcl-blue" />
              <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>
            </p>
            <p className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-bcl-blue" />
              <a className="underline" href={`tel:${SUPPORT_PHONE.replace(/\s/g, '')}`}>
                {SUPPORT_PHONE}
              </a>
            </p>
            <Button asChild variant="outline" className="mt-2 w-full">
              <Link to="/contact">Voir la page contact</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
